import React from "react"; // eslint-disable-line no-unused-vars
import { Grid2 as Grid, Typography, Link } from "@mui/material";
import {
  Charity,
  AddressUs,
  CodeDetails,
  Settings,
  Donation,
} from "../../../shared/types";
import {
  CNRating,
  ImpactStatements,
  Testimonials,
  DonationInputs,
  ClaimButton,
} from "./";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

export interface ItemProps {
  charity: Charity;
  allowDonation: boolean;
  codeDetails: CodeDetails;
  settings: Settings;
  donations: Donation[];
  handleOpenCodeInputs: () => void;
  handleOpenCreditCardInputs: () => void;
  handleSubmit: (donation: Donation) => void;
}

export const Item = ({
  charity,
  allowDonation,
  codeDetails,
  settings,
  donations,
  handleOpenCodeInputs,
  handleOpenCreditCardInputs,
  handleSubmit,
}: ItemProps): React.JSX.Element => {
  let address: AddressUs = JSON.parse(charity.managed.displayAddress);

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Grid size={{ xs: 12 }} container spacing={2}>
      {charity.managed.displayBannerUrl && (
        <Grid size={{ xs: 12 }}>
          <img
            src={charity.managed.displayBannerUrl}
            alt={`${charity.managed.displayName} Banner`}
          />
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 2 }}>
            {charity.managed.displayLogoUrl && (
              <img
                src={charity.managed.displayLogoUrl}
                style={{ width: "100%", maxWidth: "75px" }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 10 }} sx={{ margin: "auto" }}>
            <h5
              style={{
                marginLeft: mobile ? "8px" : "-18px",
                fontFamily: "Lato",
                fontWeight: "400",
                fontSize: "24px",
              }}
            >
              {charity.managed.displayName}
            </h5>
          </Grid>
          <Grid size={{ xs: 12 }} container>
            {charity.managed.displayDBA && (
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="body1"
                  component="div"
                >{`DBA: ${charity.managed.displayDBA}`}</Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="body1" component="div">
                {`EIN: ${charity.ein
                  .split("-")[0]
                  .slice(0, 2)}-${charity.ein.split("-")[0].slice(2)} ${
                  charity.chapter
                }`}
                &nbsp;
                <ClaimButton />
              </Typography>
            </Grid>
          </Grid>
          {address && (
            <Grid size={{ xs: 12 }} sx={{ margin: "auto" }}>
              {address.company && (
                <Typography
                  variant="body1"
                  component="div"
                >{`${address.company}`}</Typography>
              )}
              <Typography
                variant="body1"
                component="div"
              >{`${address.street}`}</Typography>
              {address.unit && (
                <Typography
                  variant="body1"
                  component="div"
                >{`${address.unit}`}</Typography>
              )}
              <Typography
                variant="body1"
                component="div"
              >{`${address.city}, ${address.state} ${address.zip}`}</Typography>
            </Grid>
          )}
          {charity.managed.displayWebsite && (
            <Grid size={{ xs: 12 }}>
              <Link href={charity.managed.displayWebsite} target="_blank">
                {charity.managed.displayWebsite}
              </Link>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <CNRating charity={charity} codeDetails={codeDetails} />
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ display: { sm: "none", xs: "block" } }}>
        {charity && allowDonation && (
          <DonationInputs
            charity={charity}
            settings={settings}
            codeDetails={codeDetails}
            donations={donations}
            handleOpenCodeInputs={() => {
              handleOpenCodeInputs();
            }}
            handleOpenCreditCardInputs={async () => {
              await handleOpenCreditCardInputs();
            }}
            handleSubmit={(donation: Donation) => {
              handleSubmit(donation);
            }}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Typography
          gutterBottom
          variant="body1"
          component="div"
          sx={{ marginTop: "6px", fontFamily: "Lato" }}
        >
          {charity.managed.description}
        </Typography>
      </Grid>
      {charity.managed.impactStatements && (
        <Grid size={{ xs: 12 }}>
          <ImpactStatements charity={charity} />
        </Grid>
      )}
      {!charity.managed.hideTestimonials && (
        <Grid size={{ xs: 12 }}>
          <Testimonials charity={charity} />
        </Grid>
      )}
    </Grid>
  );
};
